/* eslint-disable no-fallthrough */
import axios from 'axios';
import _ from 'lodash';
import { push, replace } from 'react-router-redux';
import { AppDispatch } from '../appStore';
import Constants from '../constants';
import {
	AudienceSourceType,
	ConditionSourceType,
	IAction,
	IDistributionFile,
	IEmailTemplate,
	IHasCurrentStep,
	IReportingSetupModel,
	ISurveyEditModel,
	ISurveyNote,
	IValidationModel,
	SurveyImage,
	SurveyStep,
} from '../models';
import * as DisplayLogicAPI from '../services/DisplayLogicAPI';
import * as ReportingSetupAPI from '../services/ReportingSetupAPI';
import * as SurveyAPI from '../services/SurveyAPI';
import { IApplicationState } from '../store';
import { ActionTypes } from '../store/ActionTypes';
import { loadSurveyImage } from '../store/SurveyImageSlice';
import { saveSurveyTheme } from '../store/SurveyThemeSlice';

export function doSave(dispatch: AppDispatch, state: IApplicationState, advance: boolean) {
	dispatch({ type: ActionTypes.TOAST_PROCESSING_GENERIC_NOTIFICATION });

	const { currentStep, surveyId } = state.surveyBuilder;
	switch (currentStep) {
		case SurveyStep.Basics:
			// There is no return or break from this case, so it will fall through. Is that intentional?
			// Some tests fail if we put a break statement in here.
			if (
				state.survey.surveyEditModel.audienceSource.audienceSourceType !==
				state.survey.lastSavedSurveyEditModel.audienceSource.audienceSourceType
			) {
				const sourceTypeIds = [ConditionSourceType.FileAttribute, ConditionSourceType.HierarchyNode, ConditionSourceType.HrisAttribute];
				DisplayLogicAPI.ClearDisplayLogicBySourceType(surveyId, sourceTypeIds);
			}
		case SurveyStep.Define:
		case SurveyStep.DefineQuestions:
		case SurveyStep.LogicAndLayout: {
			const api = surveyId === 0 ? SurveyAPI.addSurvey : SurveyAPI.saveSurvey;
			const surveyEditModel = { ...state.survey.surveyEditModel, currentStep };
			dispatch({ type: ActionTypes.STEPPER_SAVE_REQUESTED });
			return api(surveyEditModel)
				.then((result: IValidationModel<ISurveyEditModel>) => finishSave(dispatch, result.subject.survey.surveyId, result, advance))
				.catch((result: Error) => {
					dispatch({ type: ActionTypes.STEPPER_SAVE_FAILED, payload: result });
					dispatch({ type: ActionTypes.TOAST_WARNING_NOTIFICATION, payload: result.message });
				});
		}
		case SurveyStep.BrandingAndTheme: {
			const api = surveyId === 0 ? SurveyAPI.addSurvey : SurveyAPI.saveSurvey;
			const surveyImageState = { ...state.surveyImage };
			const surveyImage: SurveyImage = { ...state.surveyImage.surveyImage };
			const surveyEditModel = { ...state.survey.surveyEditModel, currentStep, surveyImage };

			dispatch({ type: ActionTypes.STEPPER_SAVE_REQUESTED });
			return Promise.all([api(surveyEditModel, surveyImageState.file), dispatch(saveSurveyTheme(surveyId)).unwrap()])
				.then(([surveyEditModelResult]) =>
					finishSave(dispatch, surveyEditModelResult.subject.survey.surveyId, surveyEditModelResult, advance),
				)
				.catch((result) => {
					if (axios.isAxiosError(result) && !result.response?.data.validationResults && result.response?.data.Detail) {
						dispatch({ type: ActionTypes.TOAST_WARNING_NOTIFICATION, payload: result.response.data.Detail });
						dispatch({ type: ActionTypes.STEPPER_SAVE_FAILED, payload: result.response.data });
					} else {
						dispatch({ type: ActionTypes.STEPPER_SAVE_FAILED, payload: result });
						dispatch({ type: ActionTypes.TOAST_WARNING_NOTIFICATION, payload: result.message });
					}
				});
		}
		case SurveyStep.Reporting: {
			const { reportingSetupModel } = state.reportingSetup;
			dispatch({ type: ActionTypes.STEPPER_SAVE_REQUESTED });
			return ReportingSetupAPI.save(surveyId, reportingSetupModel)
				.then((result: IValidationModel<IReportingSetupModel>) => finishSave(dispatch, surveyId, result, advance))
				.catch((result: Error) => {
					dispatch({ type: ActionTypes.STEPPER_SAVE_FAILED, payload: result });
					dispatch({ type: ActionTypes.TOAST_WARNING_NOTIFICATION, payload: result.message });
				});
		}
		default: {
			// Temporarily, we'll allow navigation without saving as things get hooked up
			if (advance) {
				goToNextStep(dispatch, currentStep, surveyId);
			}
			dispatch({ type: ActionTypes.TOAST_DISMISS_ALL });
		}
	}
}

export const finishSave = (dispatch: AppDispatch, surveyId: number, result: IValidationModel<IHasCurrentStep>, advance: boolean) => {
	const currentStep: SurveyStep = result.subject.currentStep;
	switch (result.isValid) {
		case true:
			// Depending on the current step, we'll need to emit different actions
			// The succeeded action must be caught by the survey builder
			switch (currentStep) {
				case SurveyStep.Basics:
				case SurveyStep.Define:
				case SurveyStep.LogicAndLayout:
				case SurveyStep.DefineQuestions:
					dispatch({ type: ActionTypes.SURVEY_SAVE_SUCCEEDED, payload: result.subject });
					break;
				case SurveyStep.BrandingAndTheme: {
					dispatch({ type: ActionTypes.SURVEY_SAVE_SUCCEEDED, payload: result.subject });
					if (!advance) {
						dispatch(loadSurveyImage(surveyId));
					}
					break;
				}
				case SurveyStep.Reporting:
					dispatch({ type: ActionTypes.REPORTING_SETUP_SAVE_SUCCEEDED, payload: result.subject });
					break;
			}

			// Due to order of operations, the save model needs to be updated first
			// This action is distinct from the above SAVE_SUCCEEDED action because its payload is
			// specific to what the SurveyBuilderStore needs
			dispatch({ type: ActionTypes.STEPPER_SAVE_COMPLETE, payload: { result, surveyId } });

			if (advance) {
				dispatch({ type: ActionTypes.TOAST_DISMISS_ALL });
				goToNextStep(dispatch, currentStep, surveyId);
			} else {
				dispatch({ type: ActionTypes.TOAST_SUCCESS_GENERIC_NOTIFICATION });
				dispatch(replace(getRoutePartial(currentStep, surveyId)));
			}
			break;
		case false:
			dispatch({ type: ActionTypes.STEPPER_VALIDATION_FAILED, payload: result });
			dispatch({ type: ActionTypes.TOAST_WARNING_GENERIC_NOTIFICATION });
			break;
	}
};

export const hasUnsavedChanges = (state: IApplicationState) => {
	const { currentStep } = state.surveyBuilder;
	const { surveyEditModel } = state.survey;

	switch (currentStep) {
		case SurveyStep.Basics:
		case SurveyStep.Define:
		case SurveyStep.LogicAndLayout:
		case SurveyStep.DefineQuestions: {
			const { lastSavedSurveyEditModel } = state.survey;
			return !_.isEqual(surveyEditModel, lastSavedSurveyEditModel);
		}
		case SurveyStep.BrandingAndTheme: {
			const { lastSavedSurveyEditModel } = state.survey;
			return state.surveyImage.dirty || !_.isEqual(surveyEditModel, lastSavedSurveyEditModel) || state.surveyTheme.dirty;
		}
		case SurveyStep.Distribution: {
			const audienceDetailsHasChanges = !_.isEqual(state.audienceDetails.audience, state.audienceDetails.lastSavedAudience);
			const distributionsHasChanges = _.some(
				state.distributionList.distributions,
				(d: IDistributionFile) => d.action !== undefined && d.action !== IAction.None,
			);
			let distributionHasChanges = false;
			const distribution = state.distributionList.distribution;
			if (distribution) {
				distributionHasChanges = distribution.action === IAction.Delete;
			} else {
				distributionHasChanges = state.distributionList.file !== undefined && state.distributionList.fileUploadType !== undefined;
			}
			// This is particularly used in hris/hierarchy if hierarchy selection changed
			if (
				surveyEditModel.audienceSource.audienceSourceType === AudienceSourceType.Hris ||
				surveyEditModel.audienceSource.audienceSourceType === AudienceSourceType.Hierarchy
			) {
				const distributionHierarchyState = state.distributionHierarchy;
				if (distributionHierarchyState) {
					const distributionHierarchy = distributionHierarchyState.distributionHierarchy;
					if (distributionHierarchy.hierarchySelection) {
						const selectionCriteria = JSON.parse(distributionHierarchy.hierarchySelection);
						distributionHasChanges = !_.isEqual(state.distributionHierarchy.selectionCriteria, selectionCriteria);
					}
				}
			}
			const inviteEmailHasChanges =
				state.emailTemplates.invitationEmail.action !== undefined && state.emailTemplates.invitationEmail.action !== IAction.None;
			const reminderEmailsHaveChanges = _.some(
				state.emailTemplates.reminderEmails,
				(e: IEmailTemplate) => e.action !== undefined && e.action !== IAction.None,
			);

			return (
				audienceDetailsHasChanges || distributionsHasChanges || distributionHasChanges || inviteEmailHasChanges || reminderEmailsHaveChanges
			);
		}
		case SurveyStep.Reporting: {
			const { reportingSetupModel, lastSavedReportingSetupModel } = state.reportingSetup;
			return !_.isEqual(reportingSetupModel, lastSavedReportingSetupModel);
		}
		default:
			return false;
	}
};

export const goToNextStep = (dispatch: AppDispatch, currentStep: SurveyStep, surveyId: number) => {
	if (currentStep !== SurveyStep.Confirm) {
		let nextStep;
		switch (currentStep) {
			case SurveyStep.Define:
				nextStep = SurveyStep.DefineQuestions;
				break;
			case SurveyStep.DefineQuestions:
				nextStep = SurveyStep.ManageDimensions;
				break;
			case SurveyStep.ManageDimensions:
				nextStep = SurveyStep.BrandingAndTheme;
				break;
			case SurveyStep.BrandingAndTheme:
				nextStep = SurveyStep.LogicAndLayout;
				break;
			case SurveyStep.Complete:
				nextStep = currentStep;
				break;
			default:
				nextStep = currentStep + 1;
				break;
		}
		route(dispatch, nextStep, surveyId);
	}
};
export const goToPreviousStep = (dispatch: AppDispatch, currentStep: SurveyStep, surveyId: number) => {
	if (currentStep !== SurveyStep.Basics) {
		let previousStep;
		switch (currentStep) {
			case SurveyStep.DefineQuestions:
				previousStep = SurveyStep.Define;
				break;
			case SurveyStep.BrandingAndTheme:
				previousStep = SurveyStep.ManageDimensions;
				break;
			case SurveyStep.LogicAndLayout:
				previousStep = SurveyStep.BrandingAndTheme;
				break;
			default:
				previousStep = currentStep - 1;
				break;
		}
		route(dispatch, previousStep, surveyId);
	}
};

const routePartials = {
	basics: Constants.ROUTE_BASICS,
	define: Constants.ROUTE_DEFINE_SURVEY_CONTENT,
	defineQuestions: Constants.ROUTE_DEFINE_SURVEY_QUESTIONS,
	manageDimensions: Constants.ROUTE_MANAGE_SURVEY_DIMENSIONS,
	brandingAndTheme: Constants.ROUTE_BRANDING_AND_THEME,
	logicAndLayout: Constants.ROUTE_LOGIC_AND_LAYOUT,
	distribution: Constants.ROUTE_AUDIENCES,
	reporting: Constants.ROUTE_REPORTING,
	confirm: Constants.ROUTE_CONFIRM_SCHEDULE,
	surveyAdmin: Constants.ROUTE_SURVEY_ADMIN,
};

export const getRoutePartial = (step: SurveyStep, surveyId: number) => {
	let pathName = '/{surveyId}';
	switch (step) {
		case SurveyStep.Basics:
			pathName += routePartials.basics;
			break;
		case SurveyStep.Define:
			pathName += routePartials.define;
			break;
		case SurveyStep.DefineQuestions:
			pathName += routePartials.defineQuestions;
			break;
		case SurveyStep.ManageDimensions:
			pathName += routePartials.manageDimensions;
			break;
		case SurveyStep.BrandingAndTheme:
			pathName += routePartials.brandingAndTheme;
			break;
		case SurveyStep.LogicAndLayout:
			pathName += routePartials.logicAndLayout;
			break;
		case SurveyStep.Distribution:
			pathName += routePartials.distribution;
			break;
		case SurveyStep.Reporting:
			pathName += routePartials.reporting;
			break;
		case SurveyStep.Confirm:
			pathName += routePartials.confirm;
			break;
		case SurveyStep.Complete:
		case SurveyStep.Admin:
			pathName = routePartials.surveyAdmin + '/{surveyId}';
			break;
	}
	return pathName.replace('{surveyId}', surveyId.toString());
};

export const route = (dispatch: AppDispatch, step: SurveyStep, surveyId: number, newWindow?: boolean) => {
	const routePath = getRoutePartial(step, surveyId);
	if (newWindow) {
		// protect against inconsistency in the way the SURVEYBUILDER_URL variable is configured
		const newWindowRoutePath = Constants.SURVEYBUILDER_URL.endsWith('/')
			? Constants.SURVEYBUILDER_URL + routePath.substring(1)
			: Constants.SURVEYBUILDER_URL + routePath;
		window.open(newWindowRoutePath, 'blank');
	} else {
		dispatch(push(routePath));
	}
};

export const hasNote = (notes: ISurveyNote[], step: SurveyStep) => {
	return notes && notes.findIndex((note: ISurveyNote) => note.step === step) >= 0;
};

export const SurveyBuilderHelper = {
	doSave,
	finishSave,
	route,
	goToNextStep,
	goToPreviousStep,
	hasUnsavedChanges,
	getRoutePartial,
	hasNote,
};
